import { useEffect, useState } from 'react';
import type { UnitTopic } from '../../core/topics';
import { call, type GetToken } from '../api';
import { isPlainClick, topicHref } from '../navigation';

type Loaded = { state: 'loading' } | { state: 'ready'; topics: UnitTopic[]; retry: boolean; notConfigured?: boolean };

/**
 * The published 240 topics for one unit (R12). Fetched when shown, and again when the unit's
 * standards change. A unit with no standards asks for nothing. A search that could not finish
 * shows what it found and a retry, never "no topics match". A deployment with no CGraph yet
 * says topics are not available yet: a fact, not an error, so it offers no retry.
 */
export function TopicList({ planId, unitId, standardIds, getToken, onOpen }: {
  planId: string; unitId: string; standardIds: readonly string[]; getToken: GetToken; onOpen(topic: UnitTopic): void;
}) {
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const standardsKey = [...standardIds].sort().join('|');
  useEffect(() => {
    if (!standardsKey) return;
    let live = true; const controller = new AbortController(); setLoaded({ state: 'loading' });
    void call<{ topics: UnitTopic[]; retry?: boolean; reason?: string }>(getToken, `/api/plans/${encodeURIComponent(planId)}/units/${encodeURIComponent(unitId)}/topics`, { signal: controller.signal })
      .then(data => { if (live) setLoaded({ state: 'ready', topics: data.topics, retry: data.retry === true, notConfigured: data.reason === 'content_not_configured' }); })
      .catch(() => { if (live) setLoaded({ state: 'ready', topics: [], retry: true }); });
    return () => { live = false; controller.abort(); };
  }, [planId, unitId, standardsKey, getToken, attempt]);
  if (!standardsKey) return null;
  if (loaded.state === 'loading') return <p className="topic-list-status" role="status">Finding 240 topics…</p>;
  return <div className="topic-list">
    {loaded.topics.length > 0 && <ul>{loaded.topics.map(topic => <li key={topic.id}>
      <a className="topic-button" href={topicHref(topic.id)} onClick={event => { if (isPlainClick(event)) { event.preventDefault(); onOpen(topic); } }}>{topic.title}</a>
    </li>)}</ul>}
    {loaded.notConfigured
      ? <p className="topic-list-status">240 topics are not available yet.</p>
      : loaded.retry
      ? <p className="topic-list-status" role="status">We could not load 240 topics right now. <button className="secondary" onClick={() => setAttempt(n => n + 1)}>Try again</button></p>
      : loaded.topics.length === 0 && <p className="topic-list-status">No 240 topics match this unit yet.</p>}
  </div>;
}
